import React, { lazy, Suspense } from 'react'

import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route
} from 'react-router-dom'

import {
  AcceptInviteUrl,
  ComplianceOverviewUrl,
  DashboardUrl,
  FreshbooksUrl,
  LoginUrl,
  ManageSubscriptionsUrl,
  PasswordResetUrl,
  SendPasswordResetUrl,
  SharedReportUrl,
  SignUpAboutYouUrl,
  SignUpCreateTeamUrl,
  SignUpInviteUrl,
  SignUpStepOneUrl,
  SignUpStepThreeUrl,
  SignUpStepTwoUrl,
  SignupUrl,
  SplashMessageUrl,
  StartUrl
} from 'constants/RouteUrls'
import NotFoundPage from 'routes/NotFoundPage'
import SideMenuLayout from 'routes/SideMenuLayout'
import AccountRoutes from 'routes/AccountRoutes'
import PersonalDashboardRoutes from 'routes/PersonalDashboardRoutes'
import TeamSpecificRoutes from 'routes/TeamSpecificRoutes'
import TeamsDashboardRoutes from 'routes/TeamsDashboardRoutes'
import AuthLandingPage from 'pages/auth/AuthLandingPage'
import AuthRoutes from 'routes/AuthRoutes'
import ProtectedRoute from 'routes/ProtectedRoute'
import RouteLayout from 'routes/RouteLayout'
import ErrorBoundaryRoute from 'routes/ErrorBoundaryRoute'
import { lazyLoadHelper } from 'routes/resources'
import LazyLoadingSkeleton from 'core/components/loaders/LazyLoadingSkeleton'

const SplashMessage = lazy(() =>
  lazyLoadHelper(import('pages/dashboard/home/SplashMessage'))
)
const AcceptTeamInvitePage = lazy(() =>
  lazyLoadHelper(import('pages/dashboard/accept-invite/AcceptTeamInvitePage'))
)
const AboutYouPage = lazy(() =>
  lazyLoadHelper(import('pages/sign-up/AboutYouPage'))
)
const FirstStepPage = lazy(() =>
  lazyLoadHelper(import('pages/sign-up/FirstStep'))
)
const SecondStepPage = lazy(() =>
  lazyLoadHelper(import('pages/sign-up/SecondStep'))
)
const CreateTeamPage = lazy(() =>
  lazyLoadHelper(import('pages/sign-up/CreateTeamPage'))
)
const ThirdStepPage = lazy(() =>
  lazyLoadHelper(import('pages/sign-up/ThirdStep'))
)
const InviteMembersPage = lazy(() =>
  lazyLoadHelper(import('pages/sign-up/InviteMembersPage'))
)
const ReportPage = lazy(() =>
  lazyLoadHelper(import('pages/shared/report/ReportPage'))
)
const Freshbooks = lazy(() => lazyLoadHelper(import('app/Freshbooks')))
const EmailSubscriptionPage = lazy(() =>
  lazyLoadHelper(
    import('pages/account/email-subscription/EmailSubscriptionPage')
  )
)
const PasswordResetPage = lazy(() =>
  lazyLoadHelper(import('pages/auth/PasswordResetPage'))
)
const PasswordEditPage = lazy(() =>
  lazyLoadHelper(import('pages/auth/PasswordEditPage'))
)

// https://www.notion.so/everlance/Authentication-7013b73145c44586b23d0fac6c0eb9f1
export const routes = (
  <Route element={<RouteLayout />} errorElement={<ErrorBoundaryRoute />}>
    <Route
      path="/"
      element={
        <ProtectedRoute>
          <SideMenuLayout />
        </ProtectedRoute>
      }
    >
      <Route
        path="/account/compliance"
        element={<Navigate to={ComplianceOverviewUrl} replace />}
      />
      <Route path="/" element={<Navigate to={DashboardUrl} replace />} />
      <Route path="/*" element={<PersonalDashboardRoutes />} />
      <Route path="/account/*" element={<AccountRoutes />} />
      <Route path="/team/*" element={<TeamSpecificRoutes />} />
      <Route path="/teams/*" element={<TeamsDashboardRoutes />} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
    {/** Signup flowAll users land in this page*/}
    <Route
      path={SignUpAboutYouUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="about-you" />}>
          <ProtectedRoute>
            <AboutYouPage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    {/* 1099 user account flow */}
    <Route
      path={SignUpStepOneUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="sign-up" />}>
          <ProtectedRoute>
            <FirstStepPage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={SignUpStepTwoUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="sign-up" />}>
          <ProtectedRoute>
            <SecondStepPage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={SignUpStepThreeUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="sign-up" />}>
          <ProtectedRoute>
            <ThirdStepPage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    {/* Team creation screens */}
    <Route
      path={SignUpCreateTeamUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="sign-up" />}>
          <ProtectedRoute>
            <CreateTeamPage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={SignUpInviteUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="sign-up" />}>
          <ProtectedRoute>
            <InviteMembersPage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    {/** Misc routes*/}
    <Route
      path={SharedReportUrl}
      element={
        <Suspense
          fallback={
            <LazyLoadingSkeleton variant="report" isChildPage withBanner />
          }
        >
          <ReportPage />
        </Suspense>
      }
    />
    <Route
      path={SplashMessageUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="app" />}>
          <ProtectedRoute>
            <SplashMessage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={AcceptInviteUrl}
      element={
        <Suspense
          fallback={<LazyLoadingSkeleton variant="accept-invite" isChildPage />}
        >
          <ProtectedRoute>
            <AcceptTeamInvitePage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={FreshbooksUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="app" />}>
          <Freshbooks />
        </Suspense>
      }
    />
    <Route
      path={SendPasswordResetUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="auth" />}>
          <PasswordResetPage />
        </Suspense>
      }
    />
    <Route
      path={PasswordResetUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="auth" />}>
          <PasswordEditPage />
        </Suspense>
      }
    />
    <Route
      path={ManageSubscriptionsUrl}
      element={
        <Suspense fallback={<LazyLoadingSkeleton variant="auth" />}>
          <EmailSubscriptionPage />
        </Suspense>
      }
    />
    {/** Auth pages*/}
    <Route
      path="/auth/"
      element={
        <ProtectedRoute validateAuthOnly>
          <AuthLandingPage />
        </ProtectedRoute>
      }
    >
      <Route path="*" element={<AuthRoutes />} />
    </Route>
    {/** Legacy routes maintenance  */}
    <Route path="/start" element={<Navigate to={StartUrl} replace />} />
    <Route path="/login" element={<Navigate to={LoginUrl} replace />} />
    <Route path="/signup" element={<Navigate to={SignupUrl} replace />} />
    <Route path="*" element={<NotFoundPage />} />
  </Route>
)

export const appRoutes = createBrowserRouter(createRoutesFromElements(routes))
